import { Ref, useImperativeHandle } from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';

export interface FormSubmitHandle<TFieldValues extends FieldValues> {
  onSubmit: () => Promise<TFieldValues | undefined>;
  setFieldError: (field: Path<TFieldValues>, message: string) => void;
}

/**
 * Exposes a form's validated values and its server-error entry point to a parent that
 * holds it by ref: `onSubmit` resolves the values, or `undefined` when validation fails.
 */
export default function useFormSubmitHandle<TFieldValues extends FieldValues>(
  ref: Ref<FormSubmitHandle<TFieldValues>>,
  {
    handleSubmit,
    setError,
  }: Pick<UseFormReturn<TFieldValues>, 'handleSubmit' | 'setError'>,
) {
  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise<TFieldValues | undefined>((resolve) => {
          handleSubmit(
            (data) => resolve(data),
            () => resolve(undefined),
          )();
        });
      },
      setFieldError(field, message) {
        setError(field, { message });
      },
    }),
    [handleSubmit, setError],
  );
}
